import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { Avatar, Typography, styled } from "@mui/material"

import IconAvatar from "assets/icons/avatar-green.svg"

import { useAppDispatch } from "redux/hooks"
import { withClient } from "hocs/features"
import { makeUpdateClient } from "redux/features/client/clientThunk"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import { Content, Card } from "pages/dashboard/elements"
import ProfileNavigation from "components/Layout/ProfileNavigation"
import Form from "components/Client/Form"
import Title from "components/ui/Title"
import EmailConfirmation from "components/Client/EmailConfirmation"
import { ClientFormValues, ClientType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & ClientType

const ProfilePage = ({
  client,
  fetchStatus,
  updateStatus,
  fetchClient,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const {
    full_name,
    first_name,
    last_name,
    email,
    unconfirmed_email,
    unconfirmed_phone,
    phone,
    personal_code,
    address,
  } = client

  const handleSubmit = (values: ClientFormValues) => {
    const { email, phone, address_attributes } = values
    const params = {
      email,
      phone,
      address_attributes: {
        id: client?.address?.id,
        ...address_attributes,
      },
    }
    dispatch(makeUpdateClient(params))
      .then((result) => {
        dispatch(showNotice({ message: result }))
      })
      .catch((error) => {
        dispatch(showAlert({ message: error }))
      })
  }

  return (
    <Content data-testid="profile-page">
      <Title>{t("pages.profile.title")}</Title>
      <ProfileNavigation />
      <Card>
        <Image
          src={IconAvatar}
          alt={full_name}
          sx={{ width: 61, height: 61 }}
        />
        <Typography variant="subtitle2">{personal_code}</Typography>
        <Name variant="h3">{full_name}</Name>
        <Form
          initialValues={{
            first_name: first_name,
            last_name: last_name,
            email: email,
            phone: phone,
            address: address,
          }}
          confirmation={{
            unconfirmed_email: unconfirmed_email,
            unconfirmed_phone: unconfirmed_phone,
          }}
          fetchStatus={fetchStatus}
          updateStatus={updateStatus}
          onConfirmation={() => fetchClient}
          onSubmit={handleSubmit}
        />
      </Card>
      <EmailConfirmation onSuccess={() => fetchClient} />
    </Content>
  )
}

export default compose<ComponentType>(withClient)(ProfilePage)

export const Image = styled(Avatar)(({ theme }) => ({
  alignSelf: "center",
  background: theme.palette.primary.main,
  margin: "0 auto 17px",
}))

export const Name = styled(Typography)(() => ({
  fontWeight: 500,
  marginBottom: 40,
}))
